/**
 * B117 — the form primitives whose absence forced the hand-rolling
 * (FORMS.md; the 2026-08-02 click-test). The law said "use the package"
 * and the package had no answer: no Checkbox behind the .t-checkbox CSS,
 * no Fieldset/FormSection/FieldRow structure, no Slider behind the
 * design app's raw ranges. Same contract as components.tsx: real form
 * elements under restyled skins, label/control association BY
 * CONSTRUCTION, honest disabled/error affordances, t-* classes only.
 */
import type { ReactNode } from "react";
import { Chip, Input } from "./components.js";
import { Icon } from "./icons.js";
import { coordinateWords, parseCoordinate } from "./geo.js";

/** FORMS.md R2 — a field is sized by what it holds, from the scale. */
export type FieldWidth = "sm" | "md" | "full";

function describedBy(id: string, hint: string | undefined, error: string | undefined): string | undefined {
  const ids = [error === undefined ? undefined : `${id}-error`, hint === undefined ? undefined : `${id}-hint`].filter(
    (x): x is string => x !== undefined,
  );
  return ids.length === 0 ? undefined : ids.join(" ");
}

function HintAndError(props: { id: string; hint?: string | undefined; error?: string | undefined }): ReactNode {
  return (
    <>
      {props.hint === undefined ? null : (
        <span className="t-field-hint" id={`${props.id}-hint`}>
          {props.hint}
        </span>
      )}
      {props.error === undefined ? null : (
        <span className="t-field-error" id={`${props.id}-error`}>
          {props.error}
        </span>
      )}
    </>
  );
}

/* ----------------------------------------------------------- Checkbox */

/**
 * The B116 defect made component: a REAL checkbox (`appearance: none`
 * skin rides the existing .t-checkbox block), its label wrapped around
 * it so the two can never drift apart. For modes use SegmentedControl
 * (FORMS.md R6) — a checkbox is only for a genuinely optional,
 * independent, binary attribute the operator opts into.
 */
export function Checkbox(props: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
  /** B999 — a box the page refuses unticked (an acknowledgement) says so; aria, not native (Input's note). */
  required?: boolean | undefined;
}): ReactNode {
  return (
    <div className="t-field">
      <label className="t-checkbox" htmlFor={props.id}>
        <input
          type="checkbox"
          id={props.id}
          checked={props.checked}
          onChange={(e) => props.onChange(e.currentTarget.checked)}
          disabled={props.disabled === true}
          aria-invalid={props.error === undefined ? undefined : "true"}
          aria-describedby={describedBy(props.id, props.hint, props.error)}
          {...(props.required === true ? { "aria-required": true } : {})}
        />
        <span className="t-checkbox-label">{props.label}</span>
      </label>
      <HintAndError id={props.id} hint={props.hint} error={props.error} />
    </div>
  );
}

/* ------------------------------------------------------------- Switch */

/**
 * B708 — an on/off setting as the membership frames draw it (639:2,
 * 581:2): the title and its consequence on the left, the track on the
 * right. A REAL checkbox with role="switch" (announced on/off), labelled by
 * construction, hint and error wired like every package field. Use it for a
 * setting that takes effect as a state ("Photo ID at the gate"); an
 * optional attribute in a form stays a Checkbox, a mode a SegmentedControl.
 */
export function Switch(props: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
  /** Shut, and why — drawn under the hint and pointed at (FORMS.md R15). */
  disabledWhy?: string;
}): ReactNode {
  const hint = props.disabledWhy === undefined ? props.hint : props.hint === undefined ? props.disabledWhy : `${props.hint} ${props.disabledWhy}`;
  return (
    <div className="t-switch-field">
      <label className="t-switch-label" htmlFor={props.id}>
        {props.label}
      </label>
      <input
        type="checkbox"
        role="switch"
        id={props.id}
        checked={props.checked}
        onChange={(e) => props.onChange(e.currentTarget.checked)}
        disabled={props.disabled === true || props.disabledWhy !== undefined}
        aria-invalid={props.error === undefined ? undefined : "true"}
        aria-describedby={describedBy(props.id, hint, props.error)}
      />
      <HintAndError id={props.id} hint={hint} error={props.error} />
    </div>
  );
}

/**
 * B708 (leg 2) — the compact list filter 637:2 draws above the Members
 * table ("TIER All ▾"): the field's name in small caps INSIDE the box, the
 * current choice beside it, a caret. The caps word IS the <label>, so the
 * <select> is named by construction; the whole box is the hit area. Use it in
 * a filter row over a list — a form field stays a Select with its label above.
 */
export function FilterSelect(props: {
  id: string;
  label: string;
  options: readonly { readonly value: string; readonly label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** B386 — a sidecar hook on the box. */
  redline?: string;
}): ReactNode {
  return (
    <label className="t-filterselect" htmlFor={props.id} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <span className="t-filterselect-label">{props.label}</span>
      <select className="t-filterselect-control" id={props.id} value={props.value} disabled={props.disabled === true} onChange={(e) => props.onChange(e.currentTarget.value)}>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="t-filterselect-caret" aria-hidden="true">
        <Icon name="chevron-down" />
      </span>
    </label>
  );
}

/**
 * B708 (leg 2) — the filter row's search box, in FilterSelect's face: the
 * caps word names the input VISIBLY inside the box (B313 budgets hidden
 * labels to zero new ones), the placeholder says what it searches, and
 * Enter hands the query to `onSubmit` (a list that holds only part of its
 * rows asks the platform for the rest).
 */
export function FilterSearch(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onSubmit?: (value: string) => void;
  /** B386 — a sidecar hook on the box. */
  redline?: string;
}): ReactNode {
  return (
    <label className="t-filterselect t-filtersearch" htmlFor={props.id} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <span className="t-filterselect-label">{props.label}</span>
      <input
        className="t-filtersearch-control"
        id={props.id}
        type="search"
        value={props.value}
        {...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {})}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && props.onSubmit !== undefined) {
            e.preventDefault();
            props.onSubmit(e.currentTarget.value);
          }
        }}
      />
    </label>
  );
}

/* ----------------------------------------------------------- Fieldset */

/** A titled group of related choices — a real fieldset+legend, never a styled div. */
export function Fieldset(props: {
  legend: string;
  children: ReactNode;
  hint?: string;
  disabled?: boolean;
}): ReactNode {
  return (
    <fieldset className="t-fieldset" disabled={props.disabled === true}>
      <legend>{props.legend}</legend>
      {props.hint === undefined ? null : <span className="t-field-hint">{props.hint}</span>}
      {props.children}
    </fieldset>
  );
}

/* -------------------------------------------------------- FormSection */

/**
 * FORMS.md R10 by construction: a section cannot exist without its
 * one-sentence teaching line. "That sentence is the difference between
 * a form and a puzzle."
 */
export function FormSection(props: {
  title: string;
  /** One sentence, in operator words, explaining the noun this section introduces. */
  teach: string;
  /** B403 (65:2) — the section head's 8px verdict dot (the GlassCard family's). */
  dot?: "good" | "warn" | "alert" | "neutral" | "info";
  /** B667 — a group inside a RightSheet: the head and teach, no glass card around it (551:136 draws none). */
  flat?: boolean;
  /**
   * B942 — the head's OUTLINE rank when the section sits straight under the
   * page's h1 (Reports, Redemption): exposed as aria-level, so the h3 and its
   * caption register (and every selector held on the tag) stay put.
   */
  level?: 2;
  children: ReactNode;
}): ReactNode {
  return (
    <section className={props.flat === true ? "t-form-section t-form-section--flat" : "t-form-section"}>
      <h3 className="t-form-section-title" {...(props.level !== undefined ? { "aria-level": props.level } : {})}>
        {props.dot !== undefined && <span className={`t-glasscard-dot t-glasscard-dot--${props.dot}`} aria-hidden="true" />}
        {props.title}
      </h3>
      <p className="t-form-section-teach">{props.teach}</p>
      <div className="t-form-section-fields">{props.children}</div>
    </section>
  );
}

/* ----------------------------------------------------- DaysOfWeekField */

const DAY_CHIP_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/**
 * B403 #46(g) — days of week as a chip ROW, never a checkbox column. The
 * chips are real buttons with aria-pressed (the package Chip), grouped
 * under a legend so AT reads "Days it runs — Mon, pressed …". Day index
 * follows the caller's own convention (the label array is positional).
 */
export function DaysOfWeekField(props: {
  legend: string;
  /** One sentence in operator words; FORMS.md R10's teaching line. */
  hint?: string;
  /** Selected day indexes into `labels` (default Mon..Sun, 0-based). */
  value: readonly number[];
  onToggle: (day: number) => void;
  labels?: readonly string[];
}): ReactNode {
  const labels = props.labels ?? DAY_CHIP_LABELS;
  return (
    <fieldset className="t-field t-days-field">
      <legend className="t-field-label">{props.legend}</legend>
      {props.hint === undefined ? null : <span className="t-field-hint">{props.hint}</span>}
      <div className="t-days-row">
        {labels.map((label, day) => (
          <Chip key={label} label={label} pressed={props.value.includes(day)} onPress={() => props.onToggle(day)} />
        ))}
      </div>
    </fieldset>
  );
}

/* ----------------------------------------------------------- FieldRow */

/** 2–3 related short fields on one line, collapsing to a column on narrow viewports. */
/**
 * B387 — the field an operator pastes a coordinate into.
 *
 * FORMS.md R2 (ask in the operator's terms) decides its shape: nobody has
 * a "longitude" to hand, they have a pair they copied off a map, in that
 * map's order. So it takes ONE text field in "latitude, longitude" and
 * says back what it understood — `37.795° N, 122.393° W` — in the hint
 * line, in the terms a map prints. That echo is the whole a11y and craft
 * argument for the component existing: a transposed paste is a valid
 * coordinate in the wrong hemisphere, and the only moment it is visible to
 * the person who can fix it is before they save.
 *
 * The state derives from ONE parse (`parseCoordinate`), so exactly one of
 * hint/error is ever passed (the B295 note on `Input`): a refusal REPLACES
 * the teaching sentence rather than arguing with it.
 */
export function CoordinateField(props: {
  id: string;
  label: string;
  /** The raw text; the caller keeps it and parses once more when it builds. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  width?: FieldWidth;
}): ReactNode {
  const read = parseCoordinate(props.value);
  const teach = "Paste from a map: latitude first, then longitude. Leave it empty if you do not know.";
  return (
    <Input
      id={props.id}
      label={props.label}
      value={props.value}
      onChange={props.onChange}
      placeholder="37.795, -122.393"
      {...(read.ok
        ? { hint: read.value === undefined ? teach : `Reads as ${coordinateWords(read.value)}.` }
        : { error: read.error })}
      {...(props.disabled === true ? { disabled: true } : {})}
      width={props.width ?? "md"}
    />
  );
}

export function FieldRow(props: {
  children: ReactNode;
  /**
   * B403 wave-2 polish (68:2) — EQUAL HALVES that cannot wrap: flex-basis 0
   * on every child makes the line deterministic, which is the cure for the
   * measured miss where two intrinsically-wide fields (the WINDOW moment
   * pair) silently stacked on the deployed page.
   */
  split?: boolean;
  /**
   * B998 — a row drawn once per item ("Name · Sells from" for each ticket
   * type) names the item it holds: the line becomes a named group,
   * announced as the cursor enters it, with no visual change. Without it
   * every row's fields read the same names over again.
   */
  group?: string;
}): ReactNode {
  return (
    <div
      className={props.split === true ? "t-field-row t-field-row--split" : "t-field-row"}
      {...(props.group === undefined ? {} : { role: "group", "aria-label": props.group })}
    >
      {props.children}
    </div>
  );
}

/* ------------------------------------------------------------- Slider */

/** A real range input restyled on the tokens — the design app's three raw ranges come home. */
export function Slider(props: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  step?: number;
  /** Names the value for humans ("16px", "1.2×") — shown live beside the label. */
  format?: (value: number) => string;
  hint?: string;
  disabled?: boolean;
}): ReactNode {
  const shown = props.format === undefined ? String(props.value) : props.format(props.value);
  return (
    <div className="t-field">
      <span className="t-slider-head">
        <label className="t-field-label" htmlFor={props.id}>
          {props.label}
        </label>
        <output className="t-slider-value" htmlFor={props.id} aria-hidden="true">
          {shown}
        </output>
      </span>
      <input
        className="t-slider"
        type="range"
        id={props.id}
        value={props.value}
        min={props.min}
        max={props.max}
        step={props.step}
        onChange={(e) => props.onChange(e.currentTarget.valueAsNumber)}
        disabled={props.disabled === true}
        aria-describedby={props.hint === undefined ? undefined : `${props.id}-hint`}
      />
      <HintAndError id={props.id} hint={props.hint} />
    </div>
  );
}
